// Centralized contextual helper content for the event creation flow.
//
// Each section key maps to:
//   - icon: a heroicons component used in the left "Hint" card
//   - titleKey + titleFallback: title shown at the top of the helper card
//   - messageKeys + messageFallbacks: ordered bullet messages
//
// To add or edit a helper:
//   1. Add the i18n keys here (and matching strings to en.json / pt.json
//      under event_create.helper.<section>.*).
//   2. Wrap the matching form block with `sectionKey="<section>"` on the
//      FormCard (or with <ContextualSection>) so it registers with the
//      ActiveSectionProvider.
//
// `stepDefaults[step]` is used when no section is active yet
// (e.g. on initial load, or when the user is between blocks).
import {
  SparklesIcon,
  CalendarDaysIcon,
  Squares2X2Icon,
  PencilSquareIcon,
  GlobeAltIcon,
  PhotoIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  WrenchScrewdriverIcon,
  BuildingStorefrontIcon,
  MapIcon,
  CurrencyEuroIcon,
  CheckCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

const k = (slug) => `event_create.helper.${slug}`;

const make = (slug, icon, titleFallback, messages) => ({
  icon,
  titleKey: k(`${slug}.title`),
  titleFallback,
  messages: messages.map((text, i) => ({
    key: k(`${slug}.m${i + 1}`),
    fallback: text,
  })),
});

export const SECTIONS = {
  // --- Step 1 ---------------------------------------------------------------
  identity: make(
    'identity',
    SparklesIcon,
    'Make the event easy to recognize',
    [
      'Use a clear public-facing name that brands will immediately understand.',
      'Add the exact location or venue so brands can judge logistics quickly.',
      'Avoid internal names or temporary labels unless they are meant to be visible.',
    ]
  ),
  schedule: make('schedule', CalendarDaysIcon, 'Set a clear schedule', [
    'Make sure the opening and closing hours match what brands will actually experience.',
    'For multi-day events, check that every day has the correct hours.',
    'Accurate dates reduce booking confusion and support questions later.',
  ]),
  categories: make('categories', Squares2X2Icon, 'Choose the best fit', [
    'Pick the categories that best reflect the audience and buying intent of the event.',
    'A precise category helps brands decide faster if this event is relevant.',
    'If the event mixes several concepts, choose the dominant one as the main category.',
  ]),
  description: make(
    'description',
    PencilSquareIcon,
    'Explain why brands should join',
    [
      'Describe the audience, atmosphere, expected movement, and what makes the event valuable.',
      'Focus on practical details: visibility, foot traffic, setup conditions, and audience profile.',
      'Keep it clear and specific instead of overly promotional.',
    ]
  ),
  webPresence: make(
    'webPresence',
    GlobeAltIcon,
    'Help brands do their homework',
    [
      'Add the official website and social handles so brands can vet the event quickly.',
      'Only link to active profiles — broken links lower trust.',
      'These fields are optional, but events with links convert noticeably better.',
    ]
  ),

  // --- Step 2 ---------------------------------------------------------------
  visuals: make('visuals', PhotoIcon, 'Help brands visualize the event', [
    'Use images that show the venue, previous editions, stands, audience, or atmosphere.',
    'Choose clean, bright shots that make the opportunity feel trustworthy.',
    'Avoid images that are too dark, blurry, or unrelated to the event.',
  ]),
  regulatory: make(
    'regulatory',
    DocumentTextIcon,
    'Share the rules upfront',
    [
      'Upload the venue or event rulebook brands must agree to before booking.',
      'Include insurance, conduct, fire safety, or any compliance requirements.',
      'Sharing this now prevents disputes after a booking is confirmed.',
    ]
  ),
  requiredDocuments: make(
    'requiredDocuments',
    ClipboardDocumentCheckIcon,
    'Collect what you need upfront',
    [
      'Required documents are things brands must provide before participating.',
      'Only ask for documents that are truly necessary.',
      'Clear document requirements reduce back-and-forth after approval.',
    ]
  ),
  amenities: make(
    'amenities',
    WrenchScrewdriverIcon,
    'Show what is included',
    [
      'Amenities help brands understand what they do or do not need to bring.',
      'Only include amenities that are actually available during the event.',
      'Clarify important setup details like electricity, tables, chairs, storage, or Wi-Fi.',
    ]
  ),

  // --- Step 3 ---------------------------------------------------------------
  spaces: make(
    'spaces',
    BuildingStorefrontIcon,
    'Define the available spaces',
    [
      'Create space options that are easy to compare — name, size, quantity, and price.',
      'Use names brands recognize quickly (e.g. Standard, Premium, Corner).',
      'Quantities control how many bookings can be made, so double-check them.',
    ]
  ),
  pricing: make('pricing', CurrencyEuroIcon, 'Keep pricing transparent', [
    'Set the stand price without taxes — taxes are added later at checkout.',
    'Make sure the price matches the size, visibility, and value of each space.',
    'Clear pricing avoids confusion during checkout and invoicing.',
  ]),

  // --- Step 4 ---------------------------------------------------------------
  floorplanUpload: make(
    'floorplanUpload',
    MapIcon,
    'Show brands the layout',
    [
      'A labelled floor plan helps brands picture exactly where their stand will be.',
      'A sketch, blueprint, or even a clear photo of a printed map works.',
      'You can skip this step and still publish — pin spaces later from the event manager.',
    ]
  ),
  floorplan: make('floorplan', Squares2X2Icon, 'Place each space carefully', [
    'Pick a space tier first, then click on the plan to drop its pin.',
    'Drag and drop the pins wherever you’d like.',
    'Scroll up and down or sideways to see the whole floor plan.',
    'Tiers without pins are still bookable — brands just won’t know the exact spot.',
  ]),

  // --- Step 5 ---------------------------------------------------------------
  reviewSummary: make(
    'reviewSummary',
    CheckCircleIcon,
    'Review before publishing',
    [
      'Check the public-facing details brands will see at first glance.',
      'Confirm dates, prices, spaces and required documents carefully.',
      'Small mistakes here can affect bookings later.',
    ]
  ),
  reviewDetails: make(
    'reviewDetails',
    InformationCircleIcon,
    'Double-check the basics',
    [
      'Make sure the name, location and descriptions read well to a brand.',
      'Verify that the chosen categories still match the event.',
      'Use the edit link to jump back to the relevant step if needed.',
    ]
  ),
  reviewMedia: make('reviewMedia', PhotoIcon, 'Confirm the visuals', [
    'The cover and gallery are the first thing brands see — make sure they represent the event well.',
    'Check that amenities and the regulatory document reflect what brands will actually find on-site.',
  ]),
  reviewSpaces: make(
    'reviewSpaces',
    BuildingStorefrontIcon,
    'Verify space inventory',
    [
      'Confirm each space tier, its quantity, size, and price.',
      'Total bookable units and max revenue should match what you expect.',
    ]
  ),
  reviewFloorplan: make('reviewFloorplan', MapIcon, 'Check the layout', [
    'Make sure every space tier is pinned where it should be on the plan.',
    'Brands will see these pins while browsing.',
  ]),
};

// Default helper shown when no section is active for a given step.
export const STEP_DEFAULTS = {
  1: 'identity',
  2: 'visuals',
  3: 'spaces',
  4: 'floorplan',
  5: 'reviewSummary',
};

export const getSectionContent = (key) => SECTIONS[key] || null;

export const getDefaultSectionForStep = (step) => STEP_DEFAULTS[step] || null;
